import React, { useState } from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";
import SabiasQue from "../SabiasQue";
import ImagenNoDisponibleKawaii from "../ImagenNoDisponibleKawaii";

// Template51: Página de Asakusa de Tokio
// Copia DIRECTA del componente Page8.jsx de guia-tokio, adaptado para usar las props del sistema guia
const Template51 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;
	const [mainError, setMainError] = useState(false);
	const [sensojiError, setSensojiError] = useState(false);
	const [nakamiseError, setNakamiseError] = useState(false);
	const [logoError, setLogoError] = useState(false);
	const [asakusaStickerError, setAsakusaStickerError] = useState(false);
	const [asakusaSticker2Error, setAsakusaSticker2Error] = useState(false);

	const logoImage = page.canvasImages?.find(
		(img) => img.id === "logo",
	)?.imageUrl;

	// Imágenes hardcodeadas directamente (valores por defecto de Tokio)
	// Intentar leer desde page.images primero, si no existe usar valores hardcodeados
	const getImageSrc = (id) => {
		if (page.images && Array.isArray(page.images)) {
			const found = page.images.find((img) => img?.id === id);
			if (found && found.src) return found.src;
		}
		return undefined;
	};

	const getCanvasImageUrl = (id) => {
		if (page.canvasImages && Array.isArray(page.canvasImages)) {
			const found = page.canvasImages.find((img) => img?.id === id);
			if (found && found.imageUrl) return found.imageUrl;
		}
		return undefined;
	};

	const mainImage = getImageSrc("asakusa-main");
	const sensojiImage = getImageSrc("sensoji");
	const nakamiseImage = getImageSrc("nakamise");
	const asakusaSticker = getCanvasImageUrl("asakusa-sticker");
	const asakusaSticker2 = getCanvasImageUrl("asakusa-sticker-2");

	// Textos desde page.texts (índices 0-11)
	const headerText = page.texts?.[0]?.content;
	const introText = page.texts?.[1]?.content;
	const heroTitle = page.texts?.[2]?.content;
	const sensoJiTitle = page.texts?.[3]?.content;
	const sensoJiText = page.texts?.[4]?.content;
	const kaminarimonTitle = page.texts?.[5]?.content;
	const kaminarimonText = page.texts?.[6]?.content;
	const nakamiseTitle = page.texts?.[7]?.content;
	const nakamiseText = page.texts?.[8]?.content;
	const sabiasQueText = page.texts?.[9]?.content;
	const comoLlegarTitle = page.texts?.[10]?.content;
	const comoLlegarContent = page.texts?.[11]?.content;

	// Lógica especial para la guía de Tokio: header #24221a en primera página, #e24217 en las siguientes
	// Lógica especial para la guía de NY: header #6C5CE7 en primera página, #59c3d8 en las siguientes
	const pageOrder =
		page.order !== undefined
			? page.order
			: page.data?.order !== undefined
				? page.data.order
				: -1;
	const isTokioGuide = headerBackgroundColor === "#24221a";
	const isNYGuide = headerBackgroundColor === "#6C5CE7";
	let actualHeaderColor = headerBackgroundColor;
	if (isTokioGuide && pageOrder > 0) {
		actualHeaderColor = "#e24217";
	} else if (isNYGuide && pageOrder > 0) {
		actualHeaderColor = "#59c3d8";
	}

	// Estilos reutilizables
	const estiloH3 = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "14px",
		fontWeight: 700,
		textTransform: "uppercase",
		margin: 0,
	};

	const contentColor = textColor || "#444";

	const estiloP = {
		fontFamily: `${fontFamily} !important`,
		fontSize: "13px",
		color: contentColor,
		margin: 0,
	};

	// Parsear el texto de SabiasQue para extraer los iconos y textos
	const parseSabiasQue = (htmlText) => {
		if (!htmlText) return [];
		const parser = new DOMParser();
		const doc = parser.parseFromString(htmlText, "text/html");
		const paragraphs = doc.querySelectorAll("p");
		const items = [];
		paragraphs.forEach((p) => {
			const text = p.textContent ?? p.innerText;
			const emojiMatch = text.match(/^([^\s]+)\s/);
			if (emojiMatch) {
				items.push({
					icono: emojiMatch[1],
					texto: text.substring(emojiMatch[1].length + 1).trim(),
				});
			}
		});
		return items;
	};

	const sabiasQueItems = parseSabiasQue(sabiasQueText);

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor,
				color: contentColor,
				position: "relative",
				fontFamily: `${fontFamily} !important`,
				"& *:not([data-template-header])": {
					fontFamily: `${fontFamily} !important`,
					color: "inherit",
				},
				"& p": {
					marginTop: "0px !important",
					marginBottom: "10px !important",
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor,
					textAlign: "center",
					padding: "15px 0",
					fontFamily: `${fontFamily}`,
					fontSize: "14px",
					fontWeight: 800,
					flexShrink: 0,
					position: "sticky",
					top: 0,
					zIndex: 1,
				}}
			>
				{headerText}
				{!isSmallScreen && logoImage && !logoError && (
					<Box
						component="img"
						src={logoImage}
						alt=""
						onError={() => setLogoError(true)}
						sx={{
							width: isSmallScreen ? 66 : 80,
							position: "absolute",
							right: 2,
							top: 18,
							zIndex: 999999,
						}}
					/>
				)}
			</Box>

			{/* IMAGEN PORTADA */}
			<Box data-block-id="image-0" sx={{ position: "relative" }}>
				{mainImage && !mainError ? (
					<img
						src={mainImage}
						alt="Asakusa"
						onError={() => setMainError(true)}
						style={{
							width: "100%",
							height: 200,
							objectFit: "cover",
						}}
					/>
				) : (
					<ImagenNoDisponibleKawaii
						width="100%"
						height={200}
						fontFamily={fontFamily}
					/>
				)}
				<Box
					component="h1"
					sx={{
						fontFamily: `${fontFamily}`,
						fontSize: isSmallScreen ? "28px" : "32px",
						textAlign: "center",
						position: "absolute",
						bottom: "0",
						margin: "10px",
						left: "20px",
						color: "#fff",
						textShadow: "2px 2px 4px rgba(0,0,0,0.6)",
					}}
				>
					{heroTitle}
				</Box>
				{asakusaSticker && !asakusaStickerError && (
					<img
						src={asakusaSticker}
						alt=""
						onError={() => setAsakusaStickerError(true)}
						style={{
							width: "90px",
							position: "absolute",
							bottom: "60px",
							left: "18px",
						}}
					/>
				)}
			</Box>

			<Box
				sx={{
					padding: "10px 12px",
					paddingBottom: "5px",
					position: "relative",
				}}
			>
				<Box
					data-block-id="text-1"
					component="div"
					dangerouslySetInnerHTML={{ __html: introText }}
					sx={{
						fontFamily: `${fontFamily} !important`,
						fontSize: 13,
						color: "#333",
						margin: 0,
						padding: 0,
					}}
				/>

				{/* BLOQUES */}
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: "0px",
						marginTop: "10px",
					}}
				>
					{/* Templo Senso-ji */}
					<Box data-block-id="text-2" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{sensojiImage && !sensojiError ? (
							<img
								src={sensojiImage}
								alt="Sensoji"
								onError={() => setSensojiError(true)}
								style={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								compact
							/>
						)}
						{sabiasQueItems.length > 0 && (
							<>
								{sabiasQueItems[0] && (
									<Box sx={{ position: "absolute", top: -10, left: "32%" }}>
										<SabiasQue
											icono={sabiasQueItems[0].icono}
											texto={sabiasQueItems[0].texto}
										/>
									</Box>
								)}
								{sabiasQueItems[1] && (
									<Box sx={{ position: "absolute", top: 32, left: "32%" }}>
										<SabiasQue
											icono={sabiasQueItems[1].icono}
											texto={sabiasQueItems[1].texto}
										/>
									</Box>
								)}
							</>
						)}
						<Box sx={{ width: "60%" }}>
							<Box
								component="h3"
								sx={{
									...estiloH3,
									fontFamily: `${fontFamily} !important`,
								}}
							>
								{sensoJiTitle}
							</Box>
							<Box
								component="div"
								dangerouslySetInnerHTML={{ __html: sensoJiText }}
								sx={{
									fontSize: 13,
									color: contentColor,
									fontFamily: `${fontFamily} !important`,
								}}
							/>
						</Box>
					</Box>

					{/* Puerta Kaminarimon */}
					<Box data-block-id="text-3" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						<Box sx={{ width: "100%" }}>
							<Box
								component="p"
								sx={{
									...estiloP,
									padding: "12px",
									backgroundColor: "#ffe3cc",
									borderRadius: "12px",
									fontFamily: `${fontFamily} !important`,
								}}
							>
								{asakusaSticker2 && !asakusaSticker2Error && (
									<img
										src={asakusaSticker2}
										alt=""
										onError={() => setAsakusaSticker2Error(true)}
										style={{
											width: isSmallScreen ? "105px" : "115px",
											float: "right",
											marginRight: "-34px",
											marginTop: isSmallScreen ? "-20px" : "-40px",
										}}
									/>
								)}
								<Box component="h3" sx={estiloH3}>
									{kaminarimonTitle}
								</Box>
								{kaminarimonText}
							</Box>
						</Box>
					</Box>

					{/* Calle Nakamise-dori */}
					<Box data-block-id="text-4" sx={{ display: "flex", gap: "16px", position: "relative" }}>
						{nakamiseImage && !nakamiseError ? (
							<img
								src={nakamiseImage}
								alt="Nakamise"
								onError={() => setNakamiseError(true)}
								style={{
									width: "40%",
									height: 110,
									objectFit: "cover",
									borderRadius: "12px",
								}}
							/>
						) : (
							<ImagenNoDisponibleKawaii
								width="40%"
								height={110}
								fontFamily={fontFamily}
								compact
							/>
						)}
						<Box sx={{ width: "60%" }}>
							<Box component="h3" sx={estiloH3}>
								{nakamiseTitle}
							</Box>
							<Box component="p" sx={estiloP}>
								{nakamiseText}
							</Box>
						</Box>
					</Box>

					{/* Cómo llegar */}
					<Box
						data-block-id="text-5"
						sx={{
							backgroundColor: "#ffe3cc",
							border: "3px solid #f28b35",
							borderRadius: "12px",
							padding: "8px 20px",
							fontSize: 13,
							fontFamily: `${fontFamily} !important`,
							color: "#333",
							marginBottom: isSmallScreen ? "12px" : 0,
							width: "100%",
						}}
					>
						<Box
							component="h4"
							sx={{
								margin: 0,
								marginBottom: "8px",
								fontSize: 13,
								fontWeight: 700,
								display: "flex",
								alignItems: "center",
								gap: 6,
								color: "#f28b35",
								textTransform: "uppercase",
								fontFamily: `${fontFamily} !important`,
							}}
						>
							{comoLlegarTitle}
						</Box>
						<Box
							component="div"
							dangerouslySetInnerHTML={{ __html: comoLlegarContent }}
							sx={{
								paddingLeft: "20px",
								margin: 0,
								lineHeight: 1.5,
								fontFamily: `${fontFamily} !important`,
							}}
						/>
					</Box>
				</Box>
			</Box>
		</Box>
	);
};

Template51.thumbnail = "/images/template-1.png";

export default Template51;
